import { DerivedDataSet, DatasetId, DataValue, ColumnId, SQLQueryOperation } from '../../types';
import { DataAccessor, DataState, DataAccessorOptions } from '../../types/data-accessor.types';
import { evaluateDerivedDataset } from './evaluateDerivedDataset';
import { useProjectStore } from '../stores/projectStore';
import logger from './logger';

/**
 * 祖先データセットを再帰的に評価する
 * DerivedDataSetの親が未評価の場合、その親も再帰的に評価してからこのデータセットを評価する
 * @param datasetId 評価対象のデータセットID
 */
async function ensureAncestorsEvaluated(datasetId: DatasetId): Promise<void> {
  const project = useProjectStore.getState().project;
  if (!project) {
    throw new Error('No project loaded');
  }

  const dataset = project.datasets[datasetId];
  if (!dataset) {
    throw new Error(`Dataset ${datasetId} not found`);
  }

  // PrimaryDataSetか、既にデータがある場合はスキップ
  if (dataset.type !== 'derived' || dataset.data) {
    return;
  }

  const derivedDataset = dataset as DerivedDataSet;

  // 親データセットを先に評価（再帰）
  for (const parentId of derivedDataset.parentDatasetIds) {
    await ensureAncestorsEvaluated(parentId);
  }

  // 最新のプロジェクト状態を取得（親の評価結果を反映するため）
  const latestProject = useProjectStore.getState().project;
  if (!latestProject) {
    throw new Error('No project loaded after ancestor evaluation');
  }

  // このデータセットを評価
  logger.info(`[ensureAncestorsEvaluated] Evaluating dataset: ${derivedDataset.name} (${datasetId})`);
  const result = await evaluateDerivedDataset(derivedDataset, latestProject.datasets);

  // 評価結果をプロジェクトストアに反映
  const updateDataset = useProjectStore.getState().updateDataset;
  updateDataset(datasetId, (d) => ({
    ...d,
    data: result.data as { [columnId: string]: DataValue[] },
    rowCount: result.rowCount,
    schema: result.schema
  }));

  logger.info(`[ensureAncestorsEvaluated] Dataset ${derivedDataset.name} evaluated successfully, rows: ${result.rowCount}`);
}

/**
 * DerivedDataSet用のDataAccessor実装
 * DerivedDataSetはデータを遅延ロードするため、非同期アクセスが基本
 */
export class DerivedDataAccessor implements DataAccessor {
  private nameToIdMap: Map<string, ColumnId>;
  private idToNameMap: Map<ColumnId, string>;
  private dataState: DataState = 'not-loaded';
  private loadPromise: Promise<DerivedDataSet> | null = null;

  constructor(
    private dataset: DerivedDataSet,
    private options: DataAccessorOptions = { autoLoad: true }
  ) {
    this.nameToIdMap = new Map();
    this.idToNameMap = new Map();
    
    // スキーマから列名→列IDマッピングを作成
    this.dataset.schema?.columns?.forEach(col => {
      this.nameToIdMap.set(col.name, col.id);
      this.idToNameMap.set(col.id, col.name);
    });

    // データの状態を初期化
    if (this.dataset.data) {
      this.dataState = 'loaded';
    }

    // 自動ロードが有効な場合
    if (this.options.autoLoad && !this.dataset.data) {
      this.loadPromise = this.loadData();
    }
  }

  private async loadData(): Promise<DerivedDataSet> {
    if (this.dataState === 'loading' || this.dataState === 'loaded') {
      return this.dataset;
    }

    this.dataState = 'loading';

    try {
      // Web版の場合、またはデータがない場合は評価を実行
      if (!this.dataset.data) {
        // プロジェクトから全データセットを取得
        const project = useProjectStore.getState().project;
        if (!project) {
          throw new Error('No project loaded');
        }

        logger.info(`[DerivedDataAccessor] Evaluating dataset: ${this.dataset.id}, name: ${this.dataset.name}`);
        const operation = this.dataset.operation;
        logger.debug(`[DerivedDataAccessor] Dataset operation:`, {
          type: operation?.type,
          query: operation?.type === 'sql_query' ? (operation as SQLQueryOperation).query?.substring(0, 100) : undefined
        });

        // 親データセット（および祖先）が未評価の場合、再帰的に評価する
        // evaluateDerivedDatasetは親が評価済みであることを前提とするため、
        // 事前に祖先全体の評価を確実にする必要がある
        for (const parentId of this.dataset.parentDatasetIds) {
          await ensureAncestorsEvaluated(parentId);
        }

        // 最新のプロジェクト状態を取得（親データセットの評価結果を反映するため）
        const updatedProject = useProjectStore.getState().project;
        if (!updatedProject) {
          throw new Error('No project loaded after parent evaluation');
        }

        // DerivedDataSetを評価（最新のdatasetsを使用）
        const evaluationResult = await evaluateDerivedDataset(this.dataset, updatedProject.datasets);

        // 新しいデータセットオブジェクトを作成（Immutable）
        const updatedDataset: DerivedDataSet = {
          ...this.dataset,
          data: evaluationResult.data as { [columnId: string]: DataValue[] },
          rowCount: evaluationResult.rowCount,
          schema: evaluationResult.schema
        };

        this.dataState = 'loaded';

        logger.info(`[DerivedDataAccessor] Dataset evaluated: ${this.dataset.id}, rows: ${evaluationResult.rowCount}`);
        return updatedDataset;
      }

      // データがある場合はそのまま使用
      if (this.dataset.data) {
        this.dataState = 'loaded';
        return this.dataset;
      } else {
        // データがない場合はエラー
        throw new Error('No data available for derived dataset');
      }
    } catch (error) {
      this.dataState = 'error';
      logger.error(`[DerivedDataAccessor] Failed to load data for dataset: ${this.dataset.id}`, error);
      if (this.options.onError) {
        this.options.onError(error as Error);
      }
      throw error;
    }
  }

  isDataAvailable(): boolean {
    // データが実際に存在するかチェック
    // dataStateがloadedでも、データがクリアされている可能性がある
    return !!this.dataset.data;
  }

  getDataState(): DataState {
    // データがクリアされた場合、状態を更新
    if (!this.dataset.data && this.dataState === 'loaded') {
      this.dataState = 'not-loaded';
    }
    return this.dataState;
  }

  async ensureDataLoaded(): Promise<DerivedDataSet> {
    // データが存在し、かつloadedの場合のみ早期リターン
    // データがクリアされた場合は再評価が必要
    if (this.dataState === 'loaded' && this.dataset.data) {
      return this.dataset;
    }

    // データがクリアされた場合、状態をリセット
    if (!this.dataset.data && this.dataState === 'loaded') {
      logger.debug(`[DerivedDataAccessor] Data cleared for dataset: ${this.dataset.id}, resetting state`);
      this.dataState = 'not-loaded';
      this.loadPromise = null;
    }

    if (this.loadPromise) {
      return this.loadPromise;
    }

    this.loadPromise = this.loadData();
    return this.loadPromise;
  }

  async getColumnData(columnIdOrName: string): Promise<DataValue[]> {
    await this.ensureDataLoaded();
    return this.getColumnDataSync(columnIdOrName);
  }

  getColumnDataSync(columnIdOrName: string): DataValue[] {
    if (!this.isDataAvailable()) {
      throw new Error('Data not loaded. Call ensureDataLoaded() first.');
    }

    // 列名の場合は列IDに変換
    const columnId = this.nameToIdMap.has(columnIdOrName)
      ? this.nameToIdMap.get(columnIdOrName)!
      : columnIdOrName as ColumnId;

    const data = this.dataset.data![columnId];
    if (!data) {
      const columnName = this.idToNameMap.get(columnId) || columnIdOrName;
      const error = new Error(`Column data for "${columnName}" (ID: ${columnId}) not found in dataset`);
      if (this.options.onError) {
        this.options.onError(error);
      }
      throw error;
    }

    return data;
  }

  async getRowData(rowIndex: number): Promise<Record<string, DataValue>> {
    await this.ensureDataLoaded();
    
    const row: Record<string, DataValue> = {};
    
    this.dataset.schema?.columns?.forEach(col => {
      const columnData = this.dataset.data![col.id];
      const value = columnData?.[rowIndex];
      row[col.name] = (value !== undefined) ? value : null;
    });
    
    return row;
  }

  async getValue(rowIndex: number, columnIdOrName: string): Promise<DataValue> {
    await this.ensureDataLoaded();

    const columnId = this.nameToIdMap.has(columnIdOrName)
      ? this.nameToIdMap.get(columnIdOrName)!
      : columnIdOrName as ColumnId;

    const columnData = this.dataset.data![columnId];
    if (!columnData) {
      const columnName = this.idToNameMap.get(columnId) || columnIdOrName;
      throw new Error(`Column data for "${columnName}" (ID: ${columnId}) not found in dataset`);
    }

    const value = columnData[rowIndex];
    return (value !== undefined) ? value : null;
  }

  getSchema() {
    return this.dataset.schema;
  }

  getColumnInfo(columnIdOrName: string) {
    const columnId = this.nameToIdMap.has(columnIdOrName) 
      ? this.nameToIdMap.get(columnIdOrName)! 
      : columnIdOrName;

    return this.dataset.schema?.columns?.find(col => 
      col.id === columnId || col.name === columnIdOrName
    );
  }

  getColumnNames(): string[] {
    return this.dataset.schema?.columns?.map(col => col.name) || [];
  }

  getRowCount(): number {
    return this.dataset.rowCount || 0;
  }

  hasColumn(columnName: string): boolean {
    return this.nameToIdMap.has(columnName);
  }

  getColumnId(columnName: string): string | undefined {
    return this.nameToIdMap.get(columnName);
  }

  getColumnName(columnId: string): string | undefined {
    return this.idToNameMap.get(columnId as ColumnId);
  }
}